import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import {BehaviorSubject, combineLatest, Subject} from 'rxjs';
import {ParameterLoaderService} from '../parameters/parameter-loader.service';
import {NgxTwikitWebPluginService} from '@twikit/ngx-twikit-web-plugin';
import {MatDialog} from '@angular/material/dialog';
import {OrderDialogComponent} from '../order-dialog/order-dialog.component';
import {delay, filter, takeUntil, tap, take} from 'rxjs/operators';
import {View} from '../camera-controls/camera-controls.component';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-product-configurator',
  templateUrl: './product-configurator.component.html',
  styleUrls: ['./product-configurator.component.scss']
})
export class ProductConfiguratorComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('visualisation', {static: false}) visualisation: ElementRef;
  @ViewChild('parametercards0', {static: false, read: ViewContainerRef}) parametercards0: ViewContainerRef;
  @ViewChild('parametercards1', {static: false, read: ViewContainerRef}) parametercards1: ViewContainerRef;
  @ViewChild('parametercards2', {static: false, read: ViewContainerRef}) parametercards2: ViewContainerRef;

  public loaded$ = new BehaviorSubject(false);
  public saving$ = new BehaviorSubject(false);
  public unsubscribe$ = new Subject<void>();
  public sidebarVisible$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public previewViewVisible$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public parameterTabs$: BehaviorSubject<{ [key: string]: any }> = new BehaviorSubject({});
  public selectedTabIndex = 0;
  public numberOfTabs = 0;
  public cameraControls: any;
  public cameraStands$: BehaviorSubject<Array<any>> = new BehaviorSubject([]);
  private exportDescriptorUrl: string = null;

  constructor(
    private readonly twikbot: NgxTwikitWebPluginService,
    private readonly parameterLoader: ParameterLoaderService,
    private readonly dialog: MatDialog) {

  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    // TODO fill in key
    const productKey = environment.productKey;

    this.twikbot.startConfigurator(productKey, this.visualisation.nativeElement)
      .then(context => {
        return context;
      })
      .catch((e) => {
        console.log('error', e);
      });


    this.twikbot.products$.pipe(take(1)).subscribe((product) => {
      fetch(product[productKey].url).then((res) => res.json()).then((res) => {
        this.exportDescriptorUrl = res.export_descriptor_url;
      });
    });

    combineLatest([this.twikbot.metaData$, this.twikbot.parametersData$])
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe(([metadata, parameters]) => {
      this.cameraControls = this.twikbot.getCameraControls();
      const staticViews: Array<any> = this.twikbot.getStaticViews();
      if (metadata.custom.staticViews) {
        metadata.custom.staticViews.forEach((sv) => {
          const cameraStands = this.cameraStands$.getValue();
          cameraStands.push({
            position: sv.position,
            cameraStand: staticViews.find((staticView) => {
              return staticView.key === sv.cameraStandName;
            })
          });
          this.cameraStands$.next(cameraStands);
        });
      }
      this.parameterTabs$.next(metadata.custom.parameterGroups);
      this.numberOfTabs = Object.keys(metadata.custom.parameterGroups).length;

      this.loadTabs(parameters, metadata.custom.parameterGroups, 0);
      this.tabGroupChanged(0);
    });


    this.previewViewVisible$.pipe(takeUntil(this.unsubscribe$)).subscribe((e) => {
      if (e) {
        // Change view
      }
    });


  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.twikbot.stopCurrentConfigurator();
  }

  public nextStep() {
    if (this.selectedTabIndex < (this.numberOfTabs - 1)) {
      this.selectedTabIndex = this.selectedTabIndex + 1;
    }
  }

  public previousStep() {
    if (this.selectedTabIndex !== 0) {
      this.selectedTabIndex = this.selectedTabIndex - 1;
    }
  }

  public toggleSidebar() {
    this.sidebarVisible$.next(!this.sidebarVisible$.getValue());
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }

  public async order() {
    this.saving$.next(true);

    const dialogRef = this.dialog.open(OrderDialogComponent, {
      width: '730px',
      data: {
        twikbot: this.twikbot,
        exportDescriptorUrl: this.exportDescriptorUrl
      }
    });

    dialogRef.afterClosed()
      .pipe(
        tap(() => this.saving$.next(false)),
        filter(result => !!result),
        delay(4000),
        takeUntil(this.unsubscribe$),
      ).subscribe(result => {
      if (result) {
        window.location.href = '/';
      }
    });
  }

  public tabGroupChanged(index) {
    this.selectedTabIndex = index;
    this.updateCameraViewByView(null, this.cameraStands$.getValue().find((cs) => cs.position === `group${index}`).cameraStand);
  }

  public updateCameraViewByView(v: View, cameraStand?) {
    const viewCameraStand = this.cameraStands$.getValue().find((cs) => cs.position === v);
    cameraStand = viewCameraStand ? viewCameraStand.cameraStand : cameraStand;
    if (cameraStand) {
      // this.cameraControls.setZoomLimitation(cameraStand.zoomDistance, cameraStand.zoomDistance);
      // this.cameraControls.setPitchLimitation(cameraStand.pitch, cameraStand.pitch);
      // this.cameraControls.setYawLimitation(cameraStand.yaw, cameraStand.yaw);
      this.cameraControls.setZoomDistance(cameraStand.zoomDistance);
      this.cameraControls.setYaw(cameraStand.yaw);
      this.cameraControls.setPitch(cameraStand.pitch);
      this.cameraControls.setRoll(cameraStand.roll);
      this.cameraControls.setFieldOfView(cameraStand.fieldOfView);
      this.cameraControls.setOrbitPoint(cameraStand.target[0], cameraStand.target[1], cameraStand.target[2]);
      this.cameraControls.setViewCenterPoint(cameraStand.target[0], cameraStand.target[1], cameraStand.target[2]);
      this.cameraControls.setClippingPlane(cameraStand.clippingPlane.near, cameraStand.clippingPlane.far);

      this.cameraControls.update();
    }
  }

  private loadTabs(parameters, parameterGroups, index) {
    switch (index) {
      case 0:
        this.parameterLoader.setRootViewContainerRef(this.parametercards0);
        break;
      case 1:
        this.parameterLoader.setRootViewContainerRef(this.parametercards1);
        break;
      case 2:
        this.parameterLoader.setRootViewContainerRef(this.parametercards2);
        break;
    }
    let paramId = null;
    let paramValue = null;
    parameterGroups['group' + index].parameters.forEach((param) => {
      const foundParameter = parameters.find((p) => p.id === param);
      this.parameterLoader.addParameter(foundParameter, value => {
        this.twikbot.setValue(foundParameter.id, value);
        paramId = foundParameter.id;
        paramValue = value;
        this.tabGroupChanged(index);
      });
    });

    if (index !== (this.numberOfTabs - 1)) {
      this.loadTabs(parameters, parameterGroups, index + 1);
    } else {
      this.loaded$.next(true);
    }
  }
}
